<script lang="ts">
    import {getLocalization} from 'core/svelte-context/context';
    import KpGenericTable from 'shared/ui-widgets/table/KpGenericTable.svelte';
    import {isFieldWithError} from 'shared/utils/record-utils';
    import type {ViewableField} from 'typings/portaro.be.types';
    import {AUTHORITY_LEADER_CODE, DA_AND_NUMBER_REGEX, DOCUMENT_LEADER_CODE, IND_1_CODE, IND_2_CODE, TOC_CODE} from 'typings/portaro.be.types';
    import {getRecordPath} from 'shared/utils/data-service-utils';

    export let fields: ViewableField[];

    const localize = getLocalization();

    function fieldCodeToViewableCode(code: string): string {
        switch (code) {
            case DOCUMENT_LEADER_CODE:
            case AUTHORITY_LEADER_CODE:
                return 'ldr';
            case IND_1_CODE:
                return 'i1';
            case IND_2_CODE:
                return 'i2';
        }
        if (DA_AND_NUMBER_REGEX.test(code)) {
            return code.substring(1);
        }
        return code;
    }

</script>

<KpGenericTable additionalClasses="marc-table"
                stripedRows
                hoverRows
                colorAccented
                columnHeadersCentered
                horizontallyDivided>

    <tr slot="header">
        <th class="header-field-number">{localize(/* @kp-localization commons.pole */ 'commons.pole')}</th>
        <th class="header-field-content">{localize(/* @kp-localization detail.obsahPole */ 'detail.obsahPole')}</th>
    </tr>

    <svelte:fragment slot="body">
        {#each fields as topField}
            {#if topField.code !== TOC_CODE}
                <tr class="field_{topField.code}" title={topField.fieldTypeText}>
                    <td class="field-code">{fieldCodeToViewableCode(topField.code)}</td>
                    {#if topField.fields.length === 0}
                        <td>
                            {#if topField.raw !== null}
                                <span class="field-value-text" title="{topField.fieldTypeText}">{topField.raw}</span>
                            {/if}
                            {#if isFieldWithError(topField)}
                                <span class="field-error-text">{topField.error.text}</span>
                            {/if}
                        </td>
                    {:else}
                        <td class="obsahPole">
                            {#each topField.fields as subfield}
                                {#if subfield.fields && subfield.fields.length > 0}
                                    {#each subfield.fields as innerSubfield}
                                        <span class="field-virtual">
                                            <span class="field_{innerSubfield.code}" title={innerSubfield.fieldTypeText}>
                                                <span class="subfield-code">{fieldCodeToViewableCode(innerSubfield.code)}</span>

                                                {#if innerSubfield.recordLink && innerSubfield.recordId}
                                                    <a href={getRecordPath(innerSubfield.recordId)} class="field-value-text">{innerSubfield.text}</a>
                                                {:else if innerSubfield.url}
                                                    <a href={innerSubfield.raw} target="_blank" class="field-value-text">{innerSubfield.raw}</a>
                                                {:else if innerSubfield.raw !== null}
                                                    <span class="field-value-text">{innerSubfield.raw}</span>
                                                {/if}
                                                {#if isFieldWithError(innerSubfield)}
                                                    <span class="field-error-text">{innerSubfield.error.text}</span>
                                                {/if}
                                            </span>
                                        </span>
                                    {/each}
                                {:else}
                                    <span class="field_{subfield.code}" title={subfield.fieldTypeText}>
                                        <span class="subfield-code">{fieldCodeToViewableCode(subfield.code)}</span>

                                        {#if subfield.recordLink && subfield.recordId}
                                            <a href={getRecordPath(subfield.recordId)} class="field-value-text">{subfield.text}</a>
                                        {:else if subfield.url}
                                            <a href={subfield.raw} target="_blank" class="field-value-text">{subfield.raw}</a>
                                        {:else if subfield.raw !== null}
                                            <span class="field-value-text">{subfield.raw}</span>
                                        {/if}
                                        {#if isFieldWithError(subfield)}
                                            <span class="field-error-text">{subfield.error.text}</span>
                                        {/if}
                                    </span>
                                {/if}
                            {/each}
                        </td>
                    {/if}
                </tr>
            {/if}
        {/each}
    </svelte:fragment>
</KpGenericTable>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    th {
        padding: @table-cell-padding @padding-small-horizontal !important;
    }

    td {
        padding: @padding-small-vertical @padding-small-horizontal !important;
    }

    .subfield-code {
        color: gray;
        font-size: 0.8em;
    }

    .field-error-text {
        color: red;
        font-size: 0.8em;
    }

    :global(.marc-table) {
        table-layout: fixed;
        width: 100%;
        border-radius: @border-radius-base;
        overflow: hidden; // To show border radius

        tr > th,
        tr > td {
            &:first-child {
                text-align: center;
                white-space: nowrap;
            }
        }

        .header-field-content {
            width: 92%;
        }
    }
</style>
